import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../ui/tooltip';
import { AlertCircle, Info, Send } from 'lucide-react';
import { SERVICES } from '../../../lib/uds/constants';
import { parseRawHex, buildGuidedRequest, encodeDidValue } from '../../../lib/uds/parser';
import { hex2 } from '../../../lib/uds/constants';

const FieldLabel = ({ children, tip }) => (
  <div className="flex items-center gap-1.5">
    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{children}</Label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" data-testid="uds-field-tip" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

const STATUS_MASK_PRESETS = [
  { value: '0xFF', label: 'All statuses (0xFF)' },
  { value: '0x08', label: 'Confirmed only (0x08)' },
  { value: '0x01', label: 'Test failed (0x01)' },
  { value: 'custom', label: 'Custom…' },
];

const RequestBuilderPanel = ({ mode, inputMode, setInputMode, profile, ecuState, onSend, lastSeed }) => {
  const [sid, setSid] = useState(0x22);
  const [subFn10, setSubFn10] = useState(0x03);
  const [subFn11, setSubFn11] = useState(0x01);
  const [statusMaskPreset, setStatusMaskPreset] = useState('0xFF');
  const [statusMaskCustom, setStatusMaskCustom] = useState('FF');
  const [snapshotCode, setSnapshotCode] = useState(profile.dtcs[0]?.code || '');
  const [readDid, setReadDid] = useState(0xf190);
  const [subFn27, setSubFn27] = useState(0x01);
  const [keyInput, setKeyInput] = useState('');
  const [writeDid, setWriteDid] = useState(profile.dids.find((d) => d.writable)?.id ?? profile.dids[0]?.id);
  const [writeValue, setWriteValue] = useState('');
  const [suppressTp, setSuppressTp] = useState(false);
  const [rawText, setRawText] = useState('22 F1 90');
  const [dtcReadKind, setDtcReadKind] = useState('status');

  useEffect(() => {
    if (mode === 'learner' && inputMode !== 'guided') setInputMode('guided');
  }, [mode, inputMode, setInputMode]);

  const writeDidMeta = profile.dids.find((d) => d.id === Number(writeDid));

  const guided = useMemo(() => {
    try {
      let bytes;
      if (sid === 0x10) bytes = buildGuidedRequest(0x10, { subFn: subFn10 });
      else if (sid === 0x11) bytes = buildGuidedRequest(0x11, { subFn: subFn11 });
      else if (sid === 0x14) bytes = buildGuidedRequest(0x14, {});
      else if (sid === 0x19) {
        if (dtcReadKind === 'snapshot') {
          const dtc = profile.dtcs.find((d) => d.code === snapshotCode);
          if (!dtc) throw new Error('Select a DTC.');
          bytes = buildGuidedRequest(0x19, { subFn: 0x04, dtcBytes: dtc.bytes, snapshotRecordNumber: 1 });
        } else {
          const mask = statusMaskPreset === 'custom' ? parseInt(statusMaskCustom, 16) : parseInt(statusMaskPreset, 16);
          if (Number.isNaN(mask)) throw new Error('Status mask must be valid hex.');
          bytes = buildGuidedRequest(0x19, { subFn: 0x02, statusMask: mask });
        }
      } else if (sid === 0x22) bytes = buildGuidedRequest(0x22, { did: Number(readDid) });
      else if (sid === 0x27) {
        if (subFn27 === 0x02) {
          const cleaned = keyInput.replace(/[^0-9a-fA-F]/g, '');
          if (cleaned.length !== 4) throw new Error('Key must be exactly 2 bytes (4 hex digits).');
          bytes = buildGuidedRequest(0x27, { subFn: 0x02, keyBytes: [parseInt(cleaned.slice(0, 2), 16), parseInt(cleaned.slice(2, 4), 16)] });
        } else {
          bytes = buildGuidedRequest(0x27, { subFn: 0x01 });
        }
      } else if (sid === 0x2e) {
        if (!writeDidMeta) throw new Error('Select a DID to write.');
        const data = encodeDidValue(writeValue, writeDidMeta.format, writeDidMeta.length);
        bytes = buildGuidedRequest(0x2e, { did: Number(writeDid), dataBytes: data });
      } else if (sid === 0x3e) bytes = buildGuidedRequest(0x3e, { suppress: suppressTp });
      return { bytes, error: null };
    } catch (e) {
      return { bytes: null, error: e.message };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid, subFn10, subFn11, dtcReadKind, statusMaskPreset, statusMaskCustom, snapshotCode, readDid, subFn27, keyInput, writeDid, writeValue, suppressTp, profile]);

  const raw = useMemo(() => parseRawHex(rawText), [rawText]);

  const activeBytes = inputMode === 'guided' ? guided.bytes : raw.bytes;
  const activeError = inputMode === 'guided' ? guided.error : raw.error;

  return (
    <Card className="technical-card h-full flex flex-col" data-testid="uds-request-builder">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-base">Request Builder</CardTitle>
        <CardDescription>Tester → ECU</CardDescription>
        <Tabs value={inputMode} onValueChange={setInputMode} className="pt-2">
          <TabsList>
            <TabsTrigger value="guided" data-testid="uds-mode-guided">Guided</TabsTrigger>
            <TabsTrigger value="raw" disabled={mode === 'learner'} data-testid="uds-mode-raw">Raw Hex</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent className="space-y-4 flex-1 overflow-y-auto">
        <TooltipProvider delayDuration={150}>
          {inputMode === 'guided' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <FieldLabel tip="The UDS Service ID selects which diagnostic operation the tester is asking the ECU to perform.">Service</FieldLabel>
                <Select value={String(sid)} onValueChange={(v) => setSid(Number(v))}>
                  <SelectTrigger data-testid="uds-service-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.sid} value={String(s.sid)}>{`0x${hex2(s.sid)} ${s.name}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {sid === 0x10 && (
                <div className="space-y-2">
                  <FieldLabel tip="Default = normal operation. Extended/Programming unlock write, erase, and security services.">Target Session</FieldLabel>
                  <Select value={String(subFn10)} onValueChange={(v) => setSubFn10(Number(v))}>
                    <SelectTrigger data-testid="uds-session-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">0x01 Default Session</SelectItem>
                      <SelectItem value="2">0x02 Programming Session</SelectItem>
                      <SelectItem value="3">0x03 Extended Diagnostic Session</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {sid === 0x11 && (
                <div className="space-y-2">
                  <FieldLabel tip="Simulates the ECU rebooting: session returns to Default and security re-locks.">Reset Type</FieldLabel>
                  <Select value={String(subFn11)} onValueChange={(v) => setSubFn11(Number(v))}>
                    <SelectTrigger data-testid="uds-reset-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">0x01 Hard Reset</SelectItem>
                      <SelectItem value="3">0x03 Soft Reset</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {sid === 0x14 && (
                <p className="text-sm text-muted-foreground">Sends group 0xFFFFFF (all DTCs) — the only group this simulator supports.</p>
              )}

              {sid === 0x19 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <FieldLabel tip="Choose whether to list DTCs matching a status mask, or pull the stored fault snapshot for one specific DTC.">Read Type</FieldLabel>
                    <Select value={dtcReadKind} onValueChange={setDtcReadKind}>
                      <SelectTrigger data-testid="uds-dtc-readtype-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="status">0x02 Report DTC By Status Mask</SelectItem>
                        <SelectItem value="snapshot">0x04 Report DTC Snapshot Record</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {dtcReadKind === 'status' ? (
                    <div className="space-y-2">
                      <FieldLabel tip="Only DTCs whose status byte shares at least one set bit with this mask are returned.">Status Mask</FieldLabel>
                      <Select value={statusMaskPreset} onValueChange={setStatusMaskPreset}>
                        <SelectTrigger data-testid="uds-status-mask-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS_MASK_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {statusMaskPreset === 'custom' && (
                        <Input value={statusMaskCustom} onChange={(e) => setStatusMaskCustom(e.target.value)} placeholder="FF" className="font-mono" data-testid="uds-status-mask-custom" />
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <FieldLabel tip="Reads a single fault snapshot record captured by the ECU when this DTC failed.">DTC</FieldLabel>
                      <Select value={snapshotCode} onValueChange={setSnapshotCode}>
                        <SelectTrigger data-testid="uds-snapshot-dtc-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {profile.dtcs.map((d) => <SelectItem key={d.code} value={d.code}>{`${d.code} — ${d.description}`}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {sid === 0x22 && (
                <div className="space-y-2">
                  <FieldLabel tip="A Data Identifier (DID) is a 2-byte address for one piece of ECU data.">Data Identifier</FieldLabel>
                  <Select value={String(readDid)} onValueChange={(v) => setReadDid(Number(v))}>
                    <SelectTrigger data-testid="uds-read-did-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {profile.dids.map((d) => <SelectItem key={d.id} value={String(d.id)}>{`0x${hex2(d.id >> 8)}${hex2(d.id & 0xff)} ${d.name}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {sid === 0x27 && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <FieldLabel tip="Request Seed asks the ECU for a random challenge. Send Key answers it to unlock.">Sub-function</FieldLabel>
                    <Select value={String(subFn27)} onValueChange={(v) => setSubFn27(Number(v))}>
                      <SelectTrigger data-testid="uds-security-subfn-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">0x01 Request Seed</SelectItem>
                        <SelectItem value="2">0x02 Send Key</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {subFn27 === 0x02 && (
                    <div className="space-y-2">
                      <FieldLabel tip={`Compute KEY from the last seed using the documented algorithm (see Reference panel). Last seed: ${lastSeed ? `0x${lastSeed.map(hex2).join('')}` : 'none requested yet'}.`}>Key (4 hex digits)</FieldLabel>
                      <Input value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="e.g. 3A21" className="font-mono" data-testid="uds-key-input" />
                    </div>
                  )}
                </div>
              )}

              {sid === 0x2e && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <FieldLabel tip="Only writable DIDs can be written, and only in Extended/Programming session with security unlocked.">Data Identifier</FieldLabel>
                    <Select value={String(writeDid)} onValueChange={(v) => setWriteDid(Number(v))}>
                      <SelectTrigger data-testid="uds-write-did-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {profile.dids.filter((d) => !d.computed).map((d) => (
                          <SelectItem key={d.id} value={String(d.id)}>{`0x${hex2(d.id >> 8)}${hex2(d.id & 0xff)} ${d.name}${d.writable ? '' : ' (read-only)'}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <FieldLabel tip={writeDidMeta ? `Format: ${writeDidMeta.format}, ${writeDidMeta.length} byte(s).` : ''}>New Value</FieldLabel>
                    <Input value={writeValue} onChange={(e) => setWriteValue(e.target.value)} placeholder={writeDidMeta?.format === 'ascii' ? 'text value' : 'hex bytes'} className="font-mono" data-testid="uds-write-value-input" />
                  </div>
                </div>
              )}

              {sid === 0x3e && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={suppressTp} onChange={(e) => setSuppressTp(e.target.checked)} data-testid="uds-suppress-checkbox" />
                  Suppress positive response (bit 0x80)
                </label>
              )}

              <div className="space-y-1.5 pt-2 border-t border-structure">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Assembled Request</Label>
                <div className="font-mono text-sm bg-muted/50 rounded-md px-3 py-2 min-h-[2.25rem]" data-testid="uds-assembled-hex">
                  {guided.bytes ? guided.bytes.map(hex2).join(' ') : '—'}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <FieldLabel tip="Type the raw request bytes, e.g. 22 F1 90. Spaces are optional.">Raw Hex Bytes</FieldLabel>
              <Textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder="e.g. 10 03 or 27 02 3A21"
                className="font-mono text-sm min-h-[90px]"
                data-testid="uds-raw-hex-input"
              />
            </div>
          )}

          {activeError && (
            <div className="flex items-center gap-2 text-sm text-destructive" data-testid="uds-request-error">
              <AlertCircle className="h-4 w-4 shrink-0" /> {activeError}
            </div>
          )}

          <Button
            onClick={() => onSend(activeBytes)}
            disabled={!activeBytes}
            className="btn-premium w-full"
            data-testid="uds-send-btn"
          >
            <Send className="h-4 w-4" /> Send
          </Button>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
};

export default RequestBuilderPanel;
